import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Repeat, Square, BatteryCharging, Flame, Brain, Sparkles, ShieldCheck, Download, Star, Apple, Smartphone } from "lucide-react";
import { PlanPicker } from "@/components/PlanPicker";
import { useAuth } from "@/context/AuthContext";
import { ContactLink } from "@/components/ContactDialog";
import { PromoBanner } from "@/components/PromoBanner";
import { WelcomeOverlay } from "@/components/WelcomeOverlay";
import { InstallAnimation } from "@/components/InstallAnimation";
import { isStandalone, getPlatform } from "@/lib/pwa";

const DIMS = [
  { icon: Compass, t: "Direzione", d: "Priorità e obiettivi chiari" },
  { icon: Repeat, t: "Continuità", d: "Costanza senza sforzo" },
  { icon: Square, t: "Spazio", d: "Confini e tempo per te" },
  { icon: BatteryCharging, t: "Ricarica", d: "Energia e recupero" },
  { icon: Flame, t: "Motivazione", d: "Slancio e visione" },
  { icon: Brain, t: "Mentalità", d: "Autostima e dialogo interno" },
];

const TESTIMONIALS = [
  { q: "In una settimana ho ritrovato una routine al mattino che non riuscivo a tenere da mesi.", n: "Giulia, 34" },
  { q: "Il test mi ha centrato in pieno. I tre momenti al giorno sono diventati un appuntamento con me stesso.", n: "Marco, 41" },
  { q: "Semplice, gentile e concreto. La serie di costanza mi motiva a non saltare un giorno.", n: "Sara, 29" },
];

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const standalone = isStandalone();
  const [demoDevice, setDemoDevice] = useState(getPlatform() === "android" ? "android" : "ios");

  return (
    <div className="pb-16" data-testid="landing-page">
      {!user && <WelcomeOverlay />}
      <header className="flex items-center justify-between px-5 pt-6">
        <div className="flex items-center gap-2.5">
          <img src="/logo-horizontal.png" alt="Coach2Go" className="h-8 w-auto object-contain" />
        </div>
        {user ? (
          <Link to="/oggi" data-testid="nav-app-link" className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-1.5 text-base font-semibold text-[#D4AF37]">Il mio percorso</Link>
        ) : (
          <Link to="/accedi" data-testid="nav-login-link" className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-1.5 text-base font-semibold text-[#D4AF37]">Accedi</Link>
        )}
      </header>
      <PromoBanner />

      <section className="px-5 pt-8">
        <motion.h1 variants={fade} custom={1} initial="hidden" animate="show" className="font-serif-el mt-3 text-center text-4xl font-semibold leading-[1.08] tracking-tight text-[#FAF9F6] sm:text-5xl" data-testid="landing-hero-title">
          Il tuo Coach<br /><span className="italic text-[#D4AF37]">sempre in tasca.</span>
        </motion.h1>
        <motion.p variants={fade} custom={2} initial="hidden" animate="show" className="mx-auto mt-4 max-w-md text-center text-base leading-relaxed text-[#B4B4BC]">
          Un breve test ti assegna il tuo profilo tra 18 archetipi. Poi ricevi un percorso guidato con tre momenti al giorno: mattino, mezzogiorno e sera.
        </motion.p>

        <motion.div variants={fade} custom={3} initial="hidden" animate="show" className="mt-7 space-y-3">
          {standalone ? (
            <button onClick={() => navigate("/avvio")} data-testid="start-assessment-button" className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base uppercase tracking-wide">
              Inizia il tuo percorso <ArrowRight size={18} />
            </button>
          ) : (
            <button onClick={() => navigate("/installa")} data-testid="landing-install-button" className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base uppercase tracking-wide">
              <Download size={18} /> Scarica l'app sul telefono
            </button>
          )}
        </motion.div>
      </section>

      <section className="mt-10 px-5">
        <p className="eyebrow">Sei dimensioni</p>
        <h2 className="font-serif-el mt-2 text-2xl font-semibold text-[#FAF9F6]">Su cosa lavoriamo insieme</h2>
        <div className="mt-5 grid grid-cols-2 gap-3">
          {DIMS.map((d, i) => (
            <motion.div key={d.t} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-2xl border border-[#27272A] bg-[#121215] p-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#18181C] text-[#D4AF37]"><d.icon size={20} /></div>
              <h3 className="mt-3 text-lg text-[#F4F4F5] font-semibold">{d.t}</h3>
              <p className="mt-1 text-base text-[#B4B4BC]">{d.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mt-10 px-5">
        <p className="eyebrow">Come funziona</p>
        <div className="mt-4 space-y-3">
          {[
            ["Rispondi a 18 domande", "Una scala da 1 a 5, meno di due minuti."],
            ["Scopri il tuo archetipo", "Ti assegniamo 1 profilo su 18 e il tuo livello."],
            ["Vivi il percorso di 7 giorni", "Tre momenti al giorno, letture e un esercizio finale."],
          ].map(([t, d], i) => (
            <div key={t} className="flex gap-4 rounded-2xl border border-[#27272A] bg-[#121215] p-4">
              <span className="tabular-nums font-semibold flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#E5C158] to-[#997A20] text-base font-bold text-[#09090B]">{i + 1}</span>
              <div>
                <h3 className="text-lg text-[#F4F4F5] font-semibold">{t}</h3>
                <p className="mt-0.5 text-base text-[#B4B4BC]">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 px-5">
        <div className="flex items-start gap-4 rounded-3xl border border-[#27272A] bg-[#121215] p-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#18181C] text-[#D4AF37]">
            <Download size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg text-[#F4F4F5] font-semibold">Installa l'app sul telefono</h3>
            <p className="mt-1 text-base leading-snug text-[#B4B4BC]">
              Aggiungila alla schermata Home per accesso rapido e promemoria. Ti guidiamo passo passo.
            </p>
            <button onClick={() => navigate("/installa")} data-testid="landing-install-link" className="btn-outline-gold mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm">
              Come installare e iniziare <ArrowRight size={15} />
            </button>
          </div>
        </div>

        {!standalone && (
          <div className="mt-4 rounded-3xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#141416] to-[#0A0A0C] p-5" data-testid="landing-install-animation">
            <p className="eyebrow text-center">Mini guida animata</p>
            <div className="mx-auto mt-3 grid max-w-[280px] grid-cols-2 gap-2 rounded-2xl border border-[#27272A] bg-[#121215] p-1.5">
              <button
                onClick={() => setDemoDevice("ios")}
                data-testid="landing-anim-ios"
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${demoDevice ==="ios" ? "bg-[#D4AF37] text-[#09090B]" : "text-[#B4B4BC]"}`}
              >
                <Apple size={14} /> iPhone
              </button>
              <button
                onClick={() => setDemoDevice("android")}
                data-testid="landing-anim-android"
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${demoDevice ==="android" ? "bg-[#D4AF37] text-[#09090B]" : "text-[#B4B4BC]"}`}
              >
                <Smartphone size={14} /> Android
              </button>
            </div>
            <div className="mt-5">
              <InstallAnimation device={demoDevice} />
            </div>
          </div>
        )}
      </section>

      <section className="mt-10 px-5">
        <div className="grid grid-cols-3 gap-3 rounded-3xl border border-[#27272A] bg-[#121215] p-5 text-center" data-testid="landing-stats">
          {[["18", "archetipi"], ["7", "giorni guidati"], ["3", "momenti al dì"]].map(([n, l]) => (
            <div key={l}>
              <p className="tabular-nums font-semibold text-3xl font-semibold text-[#D4AF37]">{n}</p>
              <p className="mt-1 text-xs uppercase tracking-wider text-[#8A8E96]">{l}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8 px-5">
        <p className="eyebrow">Chi lo usa</p>
        <h2 className="font-serif-el mt-2 text-2xl font-semibold text-[#FAF9F6]">Piccoli passi, cambiamenti veri</h2>
        <div className="mt-5 space-y-3">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={i} variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-2xl border border-[#27272A] bg-[#121215] p-4" data-testid={`testimonial-${i}`}>
              <div className="flex gap-0.5 text-[#D4AF37]">{Array.from({ length: 5 }).map((_, s) => <Star key={s} size={14} fill="#D4AF37" strokeWidth={0} />)}</div>
              <p className="mt-2 text-base italic leading-relaxed text-[#E8E4D8]">"{t.q}"</p>
              <p className="mt-2 text-sm font-semibold text-[#B4B4BC]">{t.n}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mt-10 px-5">
        <div className="rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#18181C] to-[#0D0D0F] p-6">
          <div className="flex items-center gap-2 text-[#D4AF37]">
            <Sparkles size={16} />
            <span className="text-sm font-semibold uppercase tracking-[0.2em]">7 giorni gratis</span>
          </div>
          <PlanPicker />
          <div className="mt-4 flex items-center gap-2 text-base text-[#B4B4BC]"><ShieldCheck size={16} className="text-[#D4AF37]" /> Annulli quando vuoi durante la prova.</div>
          <button onClick={() => navigate(standalone ? "/avvio" : "/installa")} data-testid="cta-bottom-button" className="btn-gold mt-5 w-full rounded-full px-6 py-3.5 text-base uppercase tracking-wide">
            {standalone ? "Inizia ora" : "Scarica l'app"}
          </button>
        </div>
      </section>
      <div className="mt-8 flex justify-center pb-10">
        <ContactLink testId="landing-contact-link" />
      </div>
    </div>
  );
}
